import { PropsWithChildren, createContext, useContext } from "react"
import { CustomerType, Product } from "../acquisitionForm/form/useAcquisitionFormSchema"
import { useAcquisitionFormPathname, useCustomerType } from "./utils"
import { ContractTypeFormValues, useStoredContractTypeFormValues } from "./contractType/contractTypeForm.utils"
import { getAcquisitionFormSteps } from "./acquisitionForm.config"
import { Outlet } from "react-router"

type AcquisitionProgressContextValue = {
  totalSteps: number
  currentStep: number
  customerType: CustomerType | null
  previousStepUrl: string
  nextStepUrl: string
  product?: Product
  // TODO do budoucna si samostatny kontext se storem
  contractInfo?: ContractTypeFormValues
  updateContractInfo?: (contractInfo: ContractTypeFormValues) => void
}

const AcquisitionProgressContext = createContext<AcquisitionProgressContextValue | null>(null)

export function useAcquisitionProgress() {
  const acquisitionProgress = useContext(AcquisitionProgressContext)
  if (!acquisitionProgress) {
    throw new Error("useAcquisitionProgress must be used within a AcquisitionProgressProvider")
  }

  return acquisitionProgress
}

export function AcqusitionProgressProvider(props: PropsWithChildren) {
  const customerType = useCustomerType()
  const [contractInfo, storeContractInfo] = useStoredContractTypeFormValues(customerType ?? "household")
  const formSteps = getAcquisitionFormSteps(contractInfo?.product ?? null)

  const totalSteps = formSteps.length
  const pathname = useAcquisitionFormPathname()

  const product = contractInfo?.product ?? "consumption"

  let stepIndex = 0
  if (customerType) {
    for (const formStep of formSteps) {
      const formStepUrl = formStep.constructUrl(customerType, product)
      if (pathname.endsWith(formStepUrl)) {
        break
      }

      stepIndex++
    }
  }

  const previousStep = formSteps[stepIndex - 1]
  const nextStep = formSteps[stepIndex + 1]

  const value: AcquisitionProgressContextValue = {
    currentStep: stepIndex + 1,
    totalSteps,
    customerType,
    previousStepUrl: !customerType || !previousStep ? "" : previousStep.constructUrl(customerType, product),
    nextStepUrl: !customerType || !nextStep ? "" : nextStep.constructUrl(customerType, product),
    product: contractInfo?.product,
    contractInfo,
    updateContractInfo: storeContractInfo,
  }

  return (
    <AcquisitionProgressContext.Provider value={value}>
      <Outlet />
      {props.children}
    </AcquisitionProgressContext.Provider>
  )
}
